import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';
import { CIP_APP_URL, CIP_RSMIN_TENANT_SLUG } from '../components/config';
import './StaffHarvestingPage.css';

const PERMIT_KEYWORDS = ['permit', 'license', 'licence', 'regulation'];

const isPermitProgramType = (programType) => {
  const values = Array.isArray(programType) ? programType : (programType ? [programType] : []);
  return values.some((value) => PERMIT_KEYWORDS.some((keyword) => String(value || '').toLowerCase().includes(keyword)));
};

const StaffHarvestingPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [modalShake, setModalShake] = useState(false);

  // ── Harvest Report (CIP-embedded reviewer dashboard) ──────────────────────
  const [connectorConfig, setConnectorConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [showLegacySubmissions, setShowLegacySubmissions] = useState(false);

  useEffect(() => {
    if (connectorConfig !== null || !configLoading) return;
    const fetchConnectorConfig = async () => {
      try {
        const res = await api.get('/api/connector/config');
        setConnectorConfig(res.data?.config || {});
      } catch {
        setConnectorConfig({});
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConnectorConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const embedCfg = connectorConfig?.embedSettings || {};
  const integrationMode = embedCfg.integrationMode === 'live' ? 'live' : 'testing';
  const cipConfiguredBase = String(embedCfg.cipBaseUrl || CIP_APP_URL || '').replace(/\/$/, '');
  const cipReportReady = Boolean(embedCfg.cipIntegrationEnabled) && !!cipConfiguredBase;
  const cipReportUrl = useMemo(() => {
    const authToken = localStorage.getItem('authToken');
    if (!cipReportReady || !authToken) return '';
    const nextPath = `/t/${CIP_RSMIN_TENANT_SLUG}/harvesting?embedded=1&embedHost=rs&mode=review&tab=dashboard&linkMode=${integrationMode}`;
    return `${cipConfiguredBase}/auth/rsmin-callback?token=${encodeURIComponent(authToken)}&next=${encodeURIComponent(nextPath)}`;
  }, [cipReportReady, cipConfiguredBase, integrationMode]);

  // ── Harvest Report (native RS data) ───────────────────────────────────────
  const [harvestSubmissions, setHarvestSubmissions] = useState([]);
  const [harvestLoading, setHarvestLoading] = useState(false);
  const [harvestError, setHarvestError] = useState('');
  const [harvestStatusFilter, setHarvestStatusFilter] = useState('all');
  const [harvestActionMessage, setHarvestActionMessage] = useState('');

  const fetchHarvestSubmissions = useCallback(async () => {
    setHarvestLoading(true);
    setHarvestError('');
    try {
      const [genRes, berryRes] = await Promise.allSettled([
        api.get('/api/data/harvesting'),
        api.get('/api/data/berry-harvesting'),
      ]);
      const combined = [];
      if (genRes.status === 'fulfilled') {
        (genRes.value?.data?.items || genRes.value?.data || []).forEach(
          (item) => combined.push({ ...item, _dataType: 'harvesting' })
        );
      }
      if (berryRes.status === 'fulfilled') {
        (berryRes.value?.data?.items || berryRes.value?.data || []).forEach(
          (item) => combined.push({ ...item, _dataType: 'berry-harvesting' })
        );
      }
      combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setHarvestSubmissions(combined);
    } catch (err) {
      setHarvestError('Failed to load harvest submissions');
    } finally {
      setHarvestLoading(false);
    }
  }, []);

  const handleApproveHarvest = async (item) => {
    try {
      await api.post(`/api/data/${item._dataType}/${item._id}/approve`);
      setHarvestActionMessage(`✓ Approved: ${item.title || item.createdByName || 'Record'}`);
      fetchHarvestSubmissions();
      setTimeout(() => setHarvestActionMessage(''), 4000);
    } catch (err) {
      setHarvestError('Failed to approve submission');
    }
  };

  const handleDeleteHarvest = async (item) => {
    const ok = window.confirm('Are you sure you want to delete this submission?');
    if (!ok) return;
    try {
      await api.delete(`/api/data/${item._dataType}/${item._id}`);
      setHarvestActionMessage(`🗑 Deleted: ${item.title || item.createdByName || 'Record'}`);
      fetchHarvestSubmissions();
      setTimeout(() => setHarvestActionMessage(''), 4000);
    } catch (err) {
      setHarvestError('Failed to delete submission');
    }
  };

  const handleBackdropClick = () => {
    setModalShake(true);
    setTimeout(() => setModalShake(false), 500);
  };
  
  // Education resources state
  const [educationResources, setEducationResources] = useState([]);
  const [educationLoading, setEducationLoading] = useState(false);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);
  const [educationFormData, setEducationFormData] = useState({
    title: '',
    category: 'Community Stories',
    description: '',
    content: '',
    thumbnailUrl: '',
    videoUrl: '',
    externalLink: '',
    tags: [],
    isPublished: false,
    isFeatured: false,
    order: 0
  });

  // Information & Permits state
  const [informationResources, setInformationResources] = useState([]);
  const [informationLoading, setInformationLoading] = useState(false);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [editingInformation, setEditingInformation] = useState(null);
  const [informationFormData, setInformationFormData] = useState({
    title: '',
    category: 'Permits & Licenses',
    description: '',
    content: '',
    thumbnailUrl: '',
    videoUrl: '',
    externalLink: '',
    tags: [],
    isPublished: false,
    isFeatured: false,
    order: 0,
    effectiveDate: '',
    expiryDate: '',
    attachments: []
  });
  const [uploadingFile, setUploadingFile] = useState(false);
  const [permitInboxCount, setPermitInboxCount] = useState(0);
  const [permitTotalCount, setPermitTotalCount] = useState(0);

  // ── Static Pages state ─────────────────────────────────────────────────────
  const [pages, setPages] = useState([]);
  const pageModalMouseDownOnOverlay = useRef(false);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [showPageModal, setShowPageModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [pageFormData, setPageFormData] = useState({
    title: '',
    subtitle: '',
    content: '',
    parentSlug: '',
    isLandingPage: false,
    showInNav: false,
    embedCipReport: false,
    isPublished: true,
    order: 0,
    attachments: []
  });
  const [uploadingPageFile, setUploadingPageFile] = useState(false);

  // Check if returning from location picker on mount
  const [activeSection, setActiveSection] = useState('posts');

  const previewPath = useMemo(() => {
    switch (activeSection) {
      case 'posts':
        return '/harvesting';
      case 'education':
        return '/harvesting/education';
      case 'permits':
        return '/harvesting/information';
      default:
        return '/harvesting';
    }
  }, [activeSection]);

  const openPreviewWindow = (viewAs) => {
    const query = new URLSearchParams({ viewAs }).toString();
    const url = `${window.location.origin}${previewPath}?${query}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const stripHtml = (value) => {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    fetchPosts();
    if (activeSection === 'education') {
      fetchEducationResources();
    }
    if (activeSection === 'permits') {
      fetchInformationResources();
      fetchPermitApplicationCounts();
    }
    if (activeSection === 'pages') {
      fetchPages();
    }
  }, [activeSection]);

  const fetchPermitApplicationCounts = useCallback(async () => {
    try {
      const response = await api.get('/api/health-program-applications?portal=harvesting');
      const permitApps = (response.data.applications || []).filter((app) =>
        app.portal === 'harvesting' && isPermitProgramType(app.programType)
      );
      setPermitTotalCount(permitApps.length);
      setPermitInboxCount(permitApps.filter((app) => app.status === 'submitted').length);
    } catch (err) {
      console.error('Error loading permit application counts:', err);
      setPermitTotalCount(0);
      setPermitInboxCount(0);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'report' && showLegacySubmissions) {
      fetchHarvestSubmissions();
    }
  }, [activeSection, showLegacySubmissions, fetchHarvestSubmissions]);



  const fetchPosts = async () => {
    try {
      console.log('Fetching all posts (staff view)...');
      const response = await api.get('/api/harvesting-posts');
      console.log('Staff posts response:', response.data);
      setPosts(response.data);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError(err.response?.data?.message || 'Error fetching posts');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await api.delete(`/api/harvesting-posts/${id}`);
        fetchPosts();
      } catch (err) {
        setError('Error deleting post');
      }
    }
  };

  const fetchEducationResources = async () => {
    try {
      setEducationLoading(true);
      setError('');
      const response = await api.get('/api/harvesting/education');
      setEducationResources(response.data.resources || []);
    } catch (err) {
      console.error('Error fetching education resources:', err);
      setError('Error loading education resources');
    } finally {
      setEducationLoading(false);
    }
  };

  const handleSaveEducation = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      if (editingEducation) {
        await api.put(`/api/harvesting/education/${editingEducation._id}`, educationFormData);
        setSuccessMessage('Education resource updated successfully!');
      } else {
        await api.post('/api/harvesting/education', educationFormData);
        setSuccessMessage('Education resource created successfully!');
      }

      setShowEducationModal(false);
      resetEducationForm();
      fetchEducationResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving education resource:', err);
      setError(err.response?.data?.error || 'Error saving education resource');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEducation = (resource) => {
    setEditingEducation(resource);
    setEducationFormData({
      title: resource.title,
      category: resource.category,
      description: resource.description,
      content: resource.content,
      thumbnailUrl: resource.thumbnailUrl || '',
      videoUrl: resource.videoUrl || '',
      externalLink: resource.externalLink || '',
      tags: resource.tags || [],
      isPublished: resource.isPublished,
      isFeatured: resource.isFeatured,
      order: resource.order || 0
    });
    setShowEducationModal(true);
  };

  const handleDeleteEducation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this education resource?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/api/harvesting/education/${id}`);
      setSuccessMessage('Education resource deleted successfully!');
      fetchEducationResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting education resource:', err);
      setError(err.response?.data?.error || 'Error deleting education resource');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      await api.patch(`/api/harvesting/education/${id}/toggle-publish`);
      fetchEducationResources();
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setError('Error updating publish status');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await api.patch(`/api/harvesting/education/${id}/toggle-featured`);
      fetchEducationResources();
    } catch (err) {
      console.error('Error toggling featured status:', err);
      setError('Error updating featured status');
    }
  };

  const resetEducationForm = () => {
    setEditingEducation(null);
    setEducationFormData({
      title: '',
      category: 'Community Stories',
      description: '',
      content: '',
      thumbnailUrl: '',
      videoUrl: '',
      externalLink: '',
      tags: [],
      isPublished: false,
      isFeatured: false,
      order: 0
    });
  };

  // Information & Permits functions
  const fetchInformationResources = async () => {
    try {
      setInformationLoading(true);
      setError('');
      const response = await api.get('/api/harvesting/information');
      setInformationResources(response.data.resources || []);
    } catch (err) {
      console.error('Error fetching information resources:', err);
      setError('Error loading information resources');
    } finally {
      setInformationLoading(false);
    }
  };

  const handleSaveInformation = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');

      if (editingInformation) {
        await api.put(`/api/harvesting/information/${editingInformation._id}`, informationFormData);
        setSuccessMessage('Information updated successfully!');
      } else {
        await api.post('/api/harvesting/information', informationFormData);
        setSuccessMessage('Information created successfully!');
      }

      setShowInformationModal(false);
      resetInformationForm();
      fetchInformationResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving information:', err);
      setError(err.response?.data?.error || 'Error saving information');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInformation = (resource) => {
    setEditingInformation(resource);
    setInformationFormData({
      title: resource.title,
      category: resource.category,
      description: resource.description,
      content: resource.content,
      thumbnailUrl: resource.thumbnailUrl || '',
      videoUrl: resource.videoUrl || '',
      externalLink: resource.externalLink || '',
      tags: resource.tags || [],
      isPublished: resource.isPublished,
      isFeatured: resource.isFeatured,
      order: resource.order || 0,
      effectiveDate: resource.effectiveDate ? resource.effectiveDate.split('T')[0] : '',
      expiryDate: resource.expiryDate ? resource.expiryDate.split('T')[0] : '',
      attachments: resource.attachments || []
    });
    setShowInformationModal(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type (allow PDFs and common document types)
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PDF and Word documents are allowed');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploadingFile(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const newAttachment = {
          filename: response.data.filename,
          url: response.data.url,
          fileType: response.data.fileType,
          size: response.data.size
        };

        setInformationFormData({
          ...informationFormData,
          attachments: [...informationFormData.attachments, newAttachment]
        });

        setSuccessMessage('File uploaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.error || 'Error uploading file');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Error uploading file');
    } finally {
      setUploadingFile(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    const newAttachments = informationFormData.attachments.filter((_, i) => i !== index);
    setInformationFormData({
      ...informationFormData,
      attachments: newAttachments
    });
  };

  const handleDeleteInformation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this information?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/api/harvesting/information/${id}`);
      setSuccessMessage('Information deleted successfully!');
      fetchInformationResources();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting information:', err);
      setError(err.response?.data?.error || 'Error deleting information');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublishInfo = async (id) => {
    try {
      await api.patch(`/api/harvesting/information/${id}/toggle-publish`);
      fetchInformationResources();
    } catch (err) {
      console.error('Error toggling publish status:', err);
      setError('Error updating publish status');
    }
  };

  const handleToggleFeaturedInfo = async (id) => {
    try {
      await api.patch(`/api/harvesting/information/${id}/toggle-featured`);
      fetchInformationResources();
    } catch (err) {
      console.error('Error toggling featured status:', err);
      setError('Error updating featured status');
    }
  };

  const resetInformationForm = () => {
    setEditingInformation(null);
    setInformationFormData({
      title: '',
      category: 'Permits & Licenses',
      description: '',
      content: '',
      thumbnailUrl: '',
      videoUrl: '',
      externalLink: '',
      tags: [],
      isPublished: false,
      isFeatured: false,
      order: 0,
      effectiveDate: '',
      expiryDate: '',
      attachments: []
    });
  };

  // ── Static Pages API functions ──────────────────────────────────────────────
  const fetchPages = async () => {
    try {
      setPagesLoading(true);
      const res = await api.get('/api/harvesting-pages');
      setPages(res.data.pages || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading pages');
    } finally {
      setPagesLoading(false);
    }
  };

  const resetPageForm = () => {
    setEditingPage(null);
    setPageFormData({ title: '', subtitle: '', content: '', parentSlug: '', isLandingPage: false, showInNav: false, embedCipReport: false, isPublished: true, order: 0, attachments: [] });
  };

  const openEditPage = (page) => {
    setEditingPage(page);
    setPageFormData({
      title: page.title,
      subtitle: page.subtitle || '',
      content: page.content || '',
      parentSlug: page.parentSlug || '',
      isLandingPage: Boolean(page.isLandingPage),
      showInNav: Boolean(page.showInNav),
      embedCipReport: Boolean(page.embedCipReport),
      isPublished: page.isPublished !== false,
      order: page.order || 0,
      attachments: page.attachments || []
    });
    setShowPageModal(true);
  };

  const handleToggleShowInNav = async (page) => {
    try {
      await api.put(`/api/harvesting-pages/${page._id}`, { showInNav: !page.showInNav });
      fetchPages();
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating page');
    }
  };

  const handlePageFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingPageFile(true);
      setError('');

      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const newAttachment = {
          filename: response.data.filename,
          url: response.data.url,
          fileType: response.data.fileType,
          size: response.data.size,
          buttonLabel: file.name.replace(/\.[^/.]+$/, '')
        };
        setPageFormData((prev) => ({ ...prev, attachments: [...prev.attachments, newAttachment] }));
        setSuccessMessage('File uploaded successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.data.error || 'Error uploading file');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Error uploading file');
    } finally {
      setUploadingPageFile(false);
      e.target.value = '';
    }
  };

  const handlePageAttachmentLabelChange = (index, buttonLabel) => {
    setPageFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.map((a, i) => (i === index ? { ...a, buttonLabel } : a))
    }));
  };

  const handleRemovePageAttachment = (index) => {
    setPageFormData((prev) => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  const handleSavePage = async () => {
    if (!pageFormData.title.trim()) return;
    try {
      setLoading(true);
      setError('');
      if (editingPage) {
        await api.put(`/api/harvesting-pages/${editingPage._id}`, pageFormData);
        setSuccessMessage('Page updated.');
      } else {
        await api.post('/api/harvesting-pages', pageFormData);
        setSuccessMessage('Page created.');
      }
      setShowPageModal(false);
      resetPageForm();
      fetchPages();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving page');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePage = async (id) => {
    if (!window.confirm('Delete this page? This cannot be undone.')) return;
    try {
      await api.delete(`/api/harvesting-pages/${id}`);
      fetchPages();
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting page');
    }
  };

  const handleInitializeDefaultPages = async () => {
    if (!window.confirm('Create the default static pages (Introduction, Recipes, Campfire Stories, Testing Information, Study Guide)? This only runs if no pages exist yet.')) return;
    try {
      await api.post('/api/harvesting-pages/initialize');
      setSuccessMessage('Default pages created.');
      fetchPages();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error initializing default pages');
    }
  };

  return (
    <div className="staff-harvesting-container">
      <div className="harvesting-header">
        <h1>Harvesting Portal Management</h1>
        <p className="subtitle">Manage harvesting resources and content</p>
      </div>

      {/* Navigation Tabs */}
      <div className="harvesting-nav">
        <button 
          className={activeSection === 'posts' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('posts')}
        >
          Posts
        </button>
        <button 
          className={activeSection === 'education' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('education')}
        >
          Harvesting Education
        </button>
        <button 
          className={activeSection === 'permits' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('permits')}
        >
          Permits
        </button>
        <button
          className={activeSection === 'pages' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('pages')}
        >
          Pages
        </button>
        <button 
          className={activeSection === 'report' ? 'nav-btn active' : 'nav-btn'}
          onClick={() => setActiveSection('report')}
        >
          Harvesting Report
        </button>
      </div>

      {activeSection !== 'report' && (
        <div className="harvesting-preview-toolbar">
          <div className="preview-toolbar-text">
            Preview this section in a new window before publishing.
          </div>
          <div className="preview-toolbar-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => openPreviewWindow('public')}
            >
              Open as Public
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => openPreviewWindow('citizen')}
            >
              Open as Citizen
            </button>
          </div>
          <div className="preview-toolbar-note">Unsaved edits are not shown in preview until you save.</div>
        </div>
      )}

      {/* Content Sections */}
      {activeSection === 'posts' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Manage Harvesting Blog Posts</h2>
            <button
              className="btn-primary"
              onClick={() => navigate('/staff/harvesting/posts/new')}
            >
              + Create Post
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}

          <div className="posts-list">
            <h3>Existing Posts ({posts.length})</h3>
            {posts.length === 0 ? (
              <p className="no-posts">No posts created yet.</p>
            ) : (
              <div className="posts-grid">
                {posts.map((post) => (
                  <div key={post._id} className="post-card">
                    {post.imageUrl && (
                      <div className="post-image">
                        <img src={post.imageUrl} alt={post.title} />
                      </div>
                    )}
                    <h4>{post.title}</h4>
                    <p className="post-content">
                      {(stripHtml(post.content) || '').substring(0, 150)}...
                    </p>
                    <div className="post-dates">
                      <p>
                        <strong>Publish:</strong>{' '}
                        {new Date(post.publishDate).toLocaleString('en-US', { timeZone: 'America/Toronto' })}
                      </p>
                      <p>
                        <strong>Unpublish:</strong>{' '}
                        {new Date(post.unpublishDate).toLocaleString('en-US', { timeZone: 'America/Toronto' })}
                      </p>
                    </div>
                    <div className="post-actions">
                      <button className="btn-edit" onClick={() => navigate(`/staff/harvesting/posts/${post._id}/edit`)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(post._id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {activeSection === 'education' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Harvesting Education Board</h2>
            <button 
              className="btn-primary" 
              onClick={() => {
                resetEducationForm();
                setShowEducationModal(true);
              }}
            >
              + Create Post
            </button>
          </div>

          <p className="section-description">
            Create and share educational content, stories, study guides, and information with your community.
            Posts can include campfire stories, traditional knowledge, test information, safety guides, and more.
          </p>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          {educationLoading ? (
            <div className="loading-spinner">Loading education resources...</div>
          ) : (
            <div className="education-resources-grid">
              {educationResources.length === 0 ? (
                <div className="empty-state">
                  <p>No posts yet. Click "Create Post" to share your first story, guide, or announcement!</p>
                </div>
              ) : (
                educationResources.map(resource => (
                  <div key={resource._id} className="education-card">
                    <div className="card-header">
                      <h3>{resource.title}</h3>
                      <div className="card-badges">
                        {resource.isFeatured && <span className="badge featured">⭐ Featured</span>}
                        <span className={`badge ${resource.isPublished ? 'published' : 'draft'}`}>
                          {resource.isPublished ? '✓ Published' : '📝 Draft'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-meta">
                      <span className="category-badge">{resource.category}</span>
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="tags">
                          {resource.tags.map((tag, idx) => (
                            <span key={idx} className="tag">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <p className="description">{resource.description}</p>

                    {resource.thumbnailUrl && (
                      <div className="thumbnail-preview">
                        <img src={resource.thumbnailUrl} alt={resource.title} />
                      </div>
                    )}

                    <div className="resource-meta">
                      {resource.videoUrl && <span className="icon-badge">🎥 Video</span>}
                      {resource.attachments && resource.attachments.length > 0 && (
                        <span className="icon-badge">📎 {resource.attachments.length} file(s)</span>
                      )}
                      {resource.externalLink && <span className="icon-badge">🔗 Link</span>}
                      <span className="views">👁 {resource.viewCount || 0} views</span>
                    </div>

                    <div className="card-actions">
                      <button 
                        className="btn-icon" 
                        onClick={() => handleEditEducation(resource)}
                        title="Edit"
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleTogglePublish(resource._id)}
                        title={resource.isPublished ? 'Unpublish' : 'Publish'}
                      >
                        {resource.isPublished ? '📥 Unpublish' : '📤 Publish'}
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleToggleFeatured(resource._id)}
                        title={resource.isFeatured ? 'Unfeature' : 'Feature'}
                      >
                        {resource.isFeatured ? '⭐ Unfeature' : '☆ Feature'}
                      </button>
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDeleteEducation(resource._id)}
                        title="Delete"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {activeSection === 'permits' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Information & Permits</h2>
            <div className="action-buttons">
              <button
                className="btn-secondary btn-with-badge"
                onClick={() => navigate('/staff/harvesting/permit-applications?view=permit')}
                title="View permit applications inbox"
              >
                View Permit Applications
                {permitInboxCount > 0 && <span className="btn-badge">{permitInboxCount}</span>}
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate('/staff/harvesting/permits/new-application-form?category=harvesting&template=permit')}
              >
                + Create Permit Application Form
              </button>
              <button 
                className="btn-primary" 
                onClick={() => {
                  resetInformationForm();
                  setShowInformationModal(true);
                }}
              >
                + Create Information
              </button>
            </div>
          </div>

          <p className="section-description">
            Manage permits, regulations, guidelines, and important harvesting information for your community.
          </p>

          <p className="section-description" style={{ marginTop: '-0.75rem', marginBottom: '1.25rem' }}>
            Permit applications in hopper: <strong>{permitInboxCount}</strong> submitted / <strong>{permitTotalCount}</strong> total permit applications.
          </p>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          {informationLoading ? (
            <div className="loading-spinner">Loading information...</div>
          ) : (
            <div className="education-resources-grid">
              {informationResources.length === 0 ? (
                <div className="empty-state">
                  <p>No information yet. Click "Create Information" to add permits, regulations, or guidelines!</p>
                </div>
              ) : (
                informationResources.map(resource => (
                  <div key={resource._id} className="education-card">
                    <div className="card-header">
                      <h3>{resource.title}</h3>
                      <div className="card-badges">
                        {resource.isFeatured && <span className="badge featured">⭐ Featured</span>}
                        <span className={`badge ${resource.isPublished ? 'published' : 'draft'}`}>
                          {resource.isPublished ? '✓ Published' : '📝 Draft'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="card-meta">
                      <span className="category-badge">{resource.category}</span>
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="tags">
                          {resource.tags.map((tag, idx) => (
                            <span key={idx} className="tag">#{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <p className="description">{resource.description}</p>

                    {resource.thumbnailUrl && (
                      <div className="thumbnail-preview">
                        <img src={resource.thumbnailUrl} alt={resource.title} />
                      </div>
                    )}

                    <div className="resource-meta">
                      {resource.videoUrl && <span className="icon-badge">🎥 Video</span>}
                      {resource.attachments && resource.attachments.length > 0 && (
                        <span className="icon-badge">📎 {resource.attachments.length} file(s)</span>
                      )}
                      {resource.externalLink && <span className="icon-badge">🔗 Link</span>}
                      <span className="views">👁 {resource.viewCount || 0} views</span>
                    </div>

                    <div className="card-actions">
                      <button 
                        className="btn-icon" 
                        onClick={() => handleEditInformation(resource)}
                        title="Edit"
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleTogglePublishInfo(resource._id)}
                        title={resource.isPublished ? 'Unpublish' : 'Publish'}
                      >
                        {resource.isPublished ? '📥 Unpublish' : '📤 Publish'}
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => handleToggleFeaturedInfo(resource._id)}
                        title={resource.isFeatured ? 'Unfeature' : 'Feature'}
                      >
                        {resource.isFeatured ? '⭐ Unfeature' : '☆ Feature'}
                      </button>
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDeleteInformation(resource._id)}
                        title="Delete"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {activeSection === 'pages' && (
        <div className="section-content">
          <div className="section-header">
            <h2>Static Pages</h2>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn-secondary" onClick={handleInitializeDefaultPages}>Initialize Defaults</button>
              <button className="btn-primary" onClick={() => { resetPageForm(); setShowPageModal(true); }}>+ Add Page</button>
            </div>
          </div>
          <p className="section-description">
            Build full static pages (Introduction, Recipes, Campfire Stories, Testing Information, Study Guide, etc.).
            The Introduction page can be set as the landing page shown on the main Harvesting Portal.
            Nest a page under another (e.g. Study Guide under Testing Information) using the parent field.
          </p>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          {pagesLoading ? (
            <div className="loading-spinner">Loading pages...</div>
          ) : pages.length === 0 ? (
            <div className="empty-state">
              <p>No pages yet. Click "Initialize Defaults" to add the standard set, or "+ Add Page" to create one.</p>
            </div>
          ) : (
            <div className="category-list">
              {pages.filter((p) => !p.parentSlug).map((page) => (
                <React.Fragment key={page._id}>
                  <div className={`category-row${page.isPublished ? '' : ' category-inactive'}`}>
                    <span className="category-icon-badge"></span>
                    <div className="category-row-info">
                      <strong>{page.title}</strong>
                      <span className="category-desc">
                        /harvesting/pages/{page.slug}{page.isLandingPage ? ' — Landing Page (shown at /harvesting)' : ''}
                        {' · Order: '}{page.order || 0}
                      </span>
                    </div>
                    <div className="category-row-actions">
                      <button
                        className={`btn-toggle${page.showInNav ? ' active' : ''}`}
                        onClick={() => handleToggleShowInNav(page)}
                        title={page.showInNav ? 'Remove from header nav' : 'Show in header nav'}
                      >
                        {page.showInNav ? '✓ In Nav' : '✗ Not in Nav'}
                      </button>
                      <Link
                        to={`/harvesting/pages/${page.slug}?viewAs=citizen`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-icon"
                      >
                        👁 Preview
                      </Link>
                      <button className="btn-icon" onClick={() => navigate(`/staff/harvesting/posts/new?pageSlug=${page.slug}`)}>📝 New Post</button>
                      <button className="btn-icon" onClick={() => openEditPage(page)}>✏️ Edit</button>
                      <button className="btn-icon btn-danger" onClick={() => handleDeletePage(page._id)}>🗑️</button>
                    </div>
                  </div>
                  {pages.filter((child) => child.parentSlug === page.slug).map((child) => (
                    <div key={child._id} className={`category-row${child.isPublished ? '' : ' category-inactive'}`} style={{ marginLeft: 28 }}>
                      <span className="category-icon-badge">↳</span>
                      <div className="category-row-info">
                        <strong>{child.title}</strong>
                        <span className="category-desc">/harvesting/pages/{page.slug}/{child.slug} · Order: {child.order || 0}</span>
                      </div>
                      <div className="category-row-actions">
                        <button
                          className={`btn-toggle${child.showInNav ? ' active' : ''}`}
                          onClick={() => handleToggleShowInNav(child)}
                          title={child.showInNav ? 'Remove from header nav' : 'Show in header nav'}
                        >
                          {child.showInNav ? '✓ In Nav' : '✗ Not in Nav'}
                        </button>
                        <Link
                          to={`/harvesting/pages/${page.slug}/${child.slug}?viewAs=citizen`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-icon"
                        >
                          👁 Preview
                        </Link>
                        <button className="btn-icon" onClick={() => navigate(`/staff/harvesting/posts/new?pageSlug=${child.slug}`)}>📝 New Post</button>
                        <button className="btn-icon" onClick={() => openEditPage(child)}>✏️ Edit</button>
                        <button className="btn-icon btn-danger" onClick={() => handleDeletePage(child._id)}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}

      {activeSection === 'report' && (
        <div className="section-content">
          <div className="report-header">
            <div>
              <h2>Harvesting Report</h2>
              <p className="section-description">
                Staff review dashboard, category/season breakdowns, and record exports — powered by CIP.
              </p>
            </div>
            <div className="report-header-actions">
              <Link to="/admin/connector-settings" className="btn-secondary">
                ⚙️ Integration Settings
              </Link>
            </div>
          </div>

          {configLoading ? (
            <div className="loading-spinner">Loading CIP integration...</div>
          ) : !cipReportReady ? (
            <div className="empty-state">
              <p>CIP integration is not enabled/configured yet.</p>
              <p>
                Enable it and set the CIP base URL in{' '}
                <Link to="/admin/connector-settings">Integration Settings</Link> to see the live
                harvesting report here.
              </p>
            </div>
          ) : !cipReportUrl ? (
            <div className="empty-state">
              <p>Could not build the CIP report link. Please log in again.</p>
            </div>
          ) : (
            <div className="cip-embed-frame-wrap">
              <iframe
                title="CIP Harvesting Report"
                src={cipReportUrl}
                className="cip-embed-frame"
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                style={{ minHeight: 720 }}
              />
            </div>
          )}

          <div className="legacy-submissions-toggle">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowLegacySubmissions((v) => !v)}
            >
              {showLegacySubmissions ? 'Hide' : 'Show'} Legacy RS Submissions (pre-CIP)
            </button>
          </div>

          {showLegacySubmissions && (
            <div className="legacy-submissions-panel">
              <div className="report-header-actions">
                <Link to="/harvesting/map" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                  🗺️ View Map
                </Link>
                <button className="btn-primary" onClick={fetchHarvestSubmissions}>
                  ↻ Refresh
                </button>
              </div>

              {/* Stats row */}
              {!harvestLoading && harvestSubmissions.length > 0 && (
                <div className="harvest-stats-row">
                  <div className="harvest-stat">
                    <span className="harvest-stat-num">{harvestSubmissions.length}</span>
                    <span className="harvest-stat-label">Total</span>
                  </div>
                  <div className="harvest-stat approved">
                    <span className="harvest-stat-num">
                      {harvestSubmissions.filter((s) => s.status === 'approved').length}
                    </span>
                    <span className="harvest-stat-label">Approved</span>
                  </div>
                </div>
              )}

              {/* Filter */}
              <div className="harvest-filter-row">
                <label htmlFor="harvest-status-filter">Filter by status:</label>
                <select
                  id="harvest-status-filter"
                  value={harvestStatusFilter}
                  onChange={(e) => setHarvestStatusFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Submissions</option>
                  <option value="approved">Approved</option>
                </select>
              </div>

              {harvestActionMessage && (
                <div className="success-message">{harvestActionMessage}</div>
              )}
              {harvestError && <div className="error-message">{harvestError}</div>}

              {harvestLoading ? (
                <div className="loading-spinner">Loading harvest submissions...</div>
              ) : harvestSubmissions.length === 0 ? (
                <div className="empty-state">
                  <p>No legacy harvest submissions found in the RS database.</p>
                </div>
              ) : (
                <div className="harvest-submission-list">
                  {harvestSubmissions
                    .filter((s) => harvestStatusFilter === 'all' || s.status === harvestStatusFilter)
                    .map((item) => (
                      <div
                        key={item._id}
                        className={`harvest-submission-card status-${item.status}`}
                      >
                        <div className="submission-card-header">
                          <div className="submission-type-badge">
                            {item._dataType === 'berry-harvesting' ? '🫐 Berry Harvest' : '🌿 General Harvest'}
                          </div>
                        </div>

                        <div className="submission-card-body">
                          <div className="submission-meta">
                            <strong>{item.title || item.createdByName || 'Unnamed Record'}</strong>
                            {item.description && (
                              <p className="submission-desc">{item.description}</p>
                            )}
                            <div className="submission-details">
                              <span>👤 {item.createdByName || 'Anonymous'}</span>
                              <span>📅 {new Date(item.createdAt).toLocaleDateString('en-CA')}</span>
                              {item.location && item.location.length === 2 && (
                                <span>
                                  📍{' '}
                                  <a
                                    href={`https://maps.google.com/?q=${item.location[0]},${item.location[1]}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {item.location[0].toFixed(4)}, {item.location[1].toFixed(4)}
                                  </a>
                                </span>
                              )}
                              {item.structuredData?.abundance && (
                                <span>🌿 Abundance: {item.structuredData.abundance}</span>
                              )}
                              {item.structuredData?.harvestSeason && (
                                <span>🗓 Season: {item.structuredData.harvestSeason}</span>
                              )}
                              {item.structuredData?.selectedIcon?.label && (
                                <span>
                                  {item.structuredData.selectedIcon.icon}{' '}
                                  {item.structuredData.selectedIcon.label}
                                </span>
                              )}
                            </div>
                            {item.freeTextData?.observations && (
                              <p className="submission-observations">
                                <em>"{item.freeTextData.observations}"</em>
                              </p>
                            )}
                          </div>

                          <div className="submission-actions">
                            {item.status !== 'approved' && (
                              <button
                                className="btn-approve"
                                onClick={() => handleApproveHarvest(item)}
                              >
                                ✓ Approve
                              </button>
                            )}
                            <button
                              className="btn-delete-harvest"
                              onClick={() => handleDeleteHarvest(item)}
                            >
                              🗑 Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                  {harvestSubmissions.filter(
                    (s) => harvestStatusFilter === 'all' || s.status === harvestStatusFilter
                  ).length === 0 && (
                    <p className="empty-filter-msg">No {harvestStatusFilter} submissions.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Education Resource Modal */}
      {showEducationModal && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content education-modal${modalShake ? ' modal-shake' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingEducation ? 'Edit Post' : 'Create New Post'}</h3>
              <button className="close-btn" onClick={() => setShowEducationModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={educationFormData.title}
                  onChange={(e) => setEducationFormData({ ...educationFormData, title: e.target.value })}
                  placeholder="Enter post title (e.g., 'My First Moose Hunt Story')"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={educationFormData.category}
                  onChange={(e) => setEducationFormData({ ...educationFormData, category: e.target.value })}
                  required
                >
                  <option value="Study Guides">📚 Study Guides</option>
                  <option value="Test Information">📝 Test Information</option>
                  <option value="Campfire Stories">🔥 Campfire Stories</option>
                  <option value="Community Stories">👥 Community Stories</option>
                  <option value="Traditional Knowledge">🌿 Traditional Knowledge</option>
                  <option value="Safety Guidelines">⚠️ Safety Guidelines</option>
                  <option value="Identification Guides">🔍 Identification Guides</option>
                  <option value="Seasonal Calendars">📅 Seasonal Calendars</option>
                  <option value="Processing & Preservation">🥫 Processing & Preservation</option>
                  <option value="Regulations & Permits">📋 Regulations & Permits</option>
                  <option value="Videos & Tutorials">🎥 Videos & Tutorials</option>
                  <option value="Downloadable Resources">📥 Downloadable Resources</option>
                  <option value="Workshop Materials">🎓 Workshop Materials</option>
                  <option value="Announcements">📢 Announcements</option>
                  <option value="Other">📍 Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Short Description *</label>
                <textarea
                  value={educationFormData.description}
                  onChange={(e) => setEducationFormData({ ...educationFormData, description: e.target.value })}
                  placeholder="Brief summary (shown in post preview)"
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <RichTextEditor
                  value={educationFormData.content}
                  onChange={(content) => setEducationFormData({ ...educationFormData, content })}
                  placeholder="Share your story, guide, or information here..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Thumbnail URL</label>
                  <input
                    type="url"
                    value={educationFormData.thumbnailUrl}
                    onChange={(e) => setEducationFormData({ ...educationFormData, thumbnailUrl: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Video URL (YouTube, Vimeo)</label>
                  <input
                    type="url"
                    value={educationFormData.videoUrl}
                    onChange={(e) => setEducationFormData({ ...educationFormData, videoUrl: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>External Link</label>
                <input
                  type="url"
                  value={educationFormData.externalLink}
                  onChange={(e) => setEducationFormData({ ...educationFormData, externalLink: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  value={educationFormData.tags.join(', ')}
                  onChange={(e) => setEducationFormData({ 
                    ...educationFormData, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  placeholder="traditional, safety, beginner"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={educationFormData.order}
                    onChange={(e) => setEducationFormData({ ...educationFormData, order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>

              <div className="form-group checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={educationFormData.isPublished}
                    onChange={(e) => setEducationFormData({ ...educationFormData, isPublished: e.target.checked })}
                  />
                  <span>Published (visible to users)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={educationFormData.isFeatured}
                    onChange={(e) => setEducationFormData({ ...educationFormData, isFeatured: e.target.checked })}
                  />
                  <span>Featured (highlighted on page)</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowEducationModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveEducation}
                disabled={!educationFormData.title || !educationFormData.description || !educationFormData.content || loading}
              >
                {loading ? 'Saving...' : (editingEducation ? 'Update Post' : 'Create Post')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information & Permits Modal */}
      {showInformationModal && (
        <div className="modal-overlay" onClick={handleBackdropClick}>
          <div className={`modal-content education-modal${modalShake ? ' modal-shake' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingInformation ? 'Edit Information' : 'Create Information'}</h3>
              <button className="close-btn" onClick={() => setShowInformationModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={informationFormData.title}
                  onChange={(e) => setInformationFormData({ ...informationFormData, title: e.target.value })}
                  placeholder="Enter title"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={informationFormData.category}
                  onChange={(e) => setInformationFormData({ ...informationFormData, category: e.target.value })}
                  required
                >
                  <option value="Permits & Licenses">📋 Permits & Licenses</option>
                  <option value="Regulations">⚖️ Regulations</option>
                  <option value="Guidelines">📖 Guidelines</option>
                  <option value="Forms & Applications">📝 Forms & Applications</option>
                  <option value="Seasonal Restrictions">📅 Seasonal Restrictions</option>
                  <option value="Bag Limits">🎯 Bag Limits</option>
                  <option value="Protected Areas">🛡️ Protected Areas</option>
                  <option value="Safety Requirements">⚠️ Safety Requirements</option>
                  <option value="Reporting Requirements">📊 Reporting Requirements</option>
                  <option value="Contact Information">📞 Contact Information</option>
                  <option value="FAQs">❓ FAQs</option>
                  <option value="Updates & Changes">🔔 Updates & Changes</option>
                  <option value="Other">📄 Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Short Description *</label>
                <textarea
                  value={informationFormData.description}
                  onChange={(e) => setInformationFormData({ ...informationFormData, description: e.target.value })}
                  placeholder="Brief summary"
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>Content *</label>
                <RichTextEditor
                  value={informationFormData.content}
                  onChange={(content) => setInformationFormData({ ...informationFormData, content })}
                  placeholder="Enter detailed information..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Thumbnail URL</label>
                  <input
                    type="url"
                    value={informationFormData.thumbnailUrl}
                    onChange={(e) => setInformationFormData({ ...informationFormData, thumbnailUrl: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label>Video URL</label>
                  <input
                    type="url"
                    value={informationFormData.videoUrl}
                    onChange={(e) => setInformationFormData({ ...informationFormData, videoUrl: e.target.value })}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </div>

              <div className="form-group">
                <label>External Link</label>
                <input
                  type="url"
                  value={informationFormData.externalLink}
                  onChange={(e) => setInformationFormData({ ...informationFormData, externalLink: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Document Attachments (PDF, Word)</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                />
                {uploadingFile && <p className="upload-status">Uploading...</p>}
                
                {informationFormData.attachments && informationFormData.attachments.length > 0 && (
                  <div className="attachments-list">
                    {informationFormData.attachments.map((attachment, index) => (
                      <div key={index} className="attachment-item">
                        <span className="attachment-icon">📄</span>
                        <span className="attachment-name">{attachment.filename}</span>
                        <span className="attachment-size">({(attachment.size / 1024).toFixed(2)} KB)</span>
                        <button
                          type="button"
                          className="remove-attachment-btn"
                          onClick={() => handleRemoveAttachment(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Effective Date</label>
                  <input
                    type="date"
                    value={informationFormData.effectiveDate}
                    onChange={(e) => setInformationFormData({ ...informationFormData, effectiveDate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={informationFormData.expiryDate}
                    onChange={(e) => setInformationFormData({ ...informationFormData, expiryDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  value={informationFormData.tags.join(', ')}
                  onChange={(e) => setInformationFormData({ 
                    ...informationFormData, 
                    tags: e.target.value.split(',').map(t => t.trim()).filter(t => t) 
                  })}
                  placeholder="permits, hunting, fishing"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={informationFormData.order}
                    onChange={(e) => setInformationFormData({ ...informationFormData, order: parseInt(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </div>

              <div className="form-group checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={informationFormData.isPublished}
                    onChange={(e) => setInformationFormData({ ...informationFormData, isPublished: e.target.checked })}
                  />
                  <span>Published (visible to users)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={informationFormData.isFeatured}
                    onChange={(e) => setInformationFormData({ ...informationFormData, isFeatured: e.target.checked })}
                  />
                  <span>Featured (highlighted on page)</span>
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => setShowInformationModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSaveInformation}
                disabled={!informationFormData.title || !informationFormData.description || !informationFormData.content || loading}
              >
                {loading ? 'Saving...' : (editingInformation ? 'Update Information' : 'Create Information')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Modal */}
      {showPageModal && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => { pageModalMouseDownOnOverlay.current = e.target === e.currentTarget; }}
          onClick={(e) => {
            if (e.target === e.currentTarget && pageModalMouseDownOnOverlay.current) {
              setShowPageModal(false);
              resetPageForm();
            }
          }}
        >
          <div className="modal-content" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingPage ? 'Edit Page' : 'Add Page'}</h3>
              <button className="close-btn" onClick={() => { setShowPageModal(false); resetPageForm(); }}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={pageFormData.title}
                  onChange={(e) => setPageFormData({ ...pageFormData, title: e.target.value })}
                  placeholder="e.g. Recipes"
                />
              </div>

              <div className="form-group">
                <label>Subtitle</label>
                <input
                  type="text"
                  value={pageFormData.subtitle}
                  onChange={(e) => setPageFormData({ ...pageFormData, subtitle: e.target.value })}
                  placeholder="Optional short subtitle shown under the title"
                />
              </div>

              <div className="form-group">
                <label>Content</label>
                <RichTextEditor
                  value={pageFormData.content}
                  onChange={(content) => setPageFormData({ ...pageFormData, content })}
                  placeholder="Write the full page content here..."
                />
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Parent Page</label>
                  <select
                    value={pageFormData.parentSlug}
                    onChange={(e) => setPageFormData({ ...pageFormData, parentSlug: e.target.value })}
                  >
                    <option value="">None (top-level page)</option>
                    {pages
                      .filter((p) => !p.parentSlug && (!editingPage || p._id !== editingPage._id))
                      .map((p) => (
                        <option key={p._id} value={p.slug}>{p.title}</option>
                      ))}
                  </select>
                  <small>e.g. Set Study Guide's parent to Testing Information</small>
                </div>

                <div className="form-group" style={{ flex: '0 0 120px' }}>
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={pageFormData.order}
                    onChange={(e) => setPageFormData({ ...pageFormData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="form-group checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={pageFormData.isPublished}
                    onChange={(e) => setPageFormData({ ...pageFormData, isPublished: e.target.checked })}
                  />
                  <span>Published (visible to users)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={pageFormData.isLandingPage}
                    disabled={Boolean(pageFormData.parentSlug)}
                    onChange={(e) => setPageFormData({ ...pageFormData, isLandingPage: e.target.checked })}
                  />
                  <span>Use as landing page (shown at /harvesting)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={pageFormData.showInNav}
                    onChange={(e) => setPageFormData({ ...pageFormData, showInNav: e.target.checked })}
                  />
                  <span>Show in header navigation (ordered by Display Order below)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={pageFormData.embedCipReport}
                    onChange={(e) => setPageFormData({ ...pageFormData, embedCipReport: e.target.checked })}
                  />
                  <span>Show CIP harvest log/map embed below this page's content</span>
                </label>
              </div>

              <div className="form-group">
                <label>File Downloads</label>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
                  Upload a file and give it a button name — it will show as a download button on this page.
                </p>
                <input
                  type="file"
                  onChange={handlePageFileUpload}
                  disabled={uploadingPageFile}
                />
                {uploadingPageFile && <p className="upload-status">Uploading...</p>}

                {pageFormData.attachments.length > 0 && (
                  <div className="attachments-list">
                    {pageFormData.attachments.map((attachment, index) => (
                      <div key={index} className="attachment-item">
                        <input
                          type="text"
                          value={attachment.buttonLabel || ''}
                          onChange={(e) => handlePageAttachmentLabelChange(index, e.target.value)}
                          placeholder="Button name (e.g. Download Study Guide PDF)"
                          style={{ flex: 1, marginRight: 8 }}
                        />
                        <span className="attachment-name">{attachment.filename}</span>
                        <button
                          type="button"
                          className="remove-attachment-btn"
                          onClick={() => handleRemovePageAttachment(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => { setShowPageModal(false); resetPageForm(); }}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleSavePage}
                disabled={!pageFormData.title.trim() || loading}
              >
                {loading ? 'Saving...' : (editingPage ? 'Update Page' : 'Create Page')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StaffHarvestingPage;
